import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, Loader2, AlertTriangle, Camera, CreditCard } from "lucide-react";
import { AiAvatar } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const IDLE_TIMEOUT = 180; // 3 minutes during scanning
const COUNTDOWN_START = 5;

export default function KioskBadgeScan() {
  const navigate = useNavigate();
  const location = useLocation();
  const registrationData = location.state?.registrationData;
  const idleTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  
  // Scan states: waiting, countdown, capturing, captured, processing, error
  const [scanState, setScanState] = useState("waiting");
  const [countdown, setCountdown] = useState(COUNTDOWN_START);
  const [capturedImages, setCapturedImages] = useState({ front: null, back: null });
  const [ocrResult, setOcrResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [progress, setProgress] = useState(0);

  const [processingMessage, setProcessingMessage] = useState("Checking badge information...");

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  useEffect(() => {
    if (!registrationData) {
      navigate("/kiosk/category");
    }
  }, [registrationData, navigate]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  if (!registrationData) {
    return null;
  }

  const startCountdown = () => {
    setScanState("countdown");
    setCountdown(COUNTDOWN_START);
    
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          captureImages();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const captureImages = async () => {
    setScanState("capturing");
    
    // Simulate camera capture from USB cameras
    // In production, this would interface with Windows USB camera API
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock captured images (in production these come from USB cameras)
    const mockFrontImage = generateMockBadgeImage("front");
    const mockBackImage = generateMockBadgeImage("back");
    
    setCapturedImages({ front: mockFrontImage, back: mockBackImage });
    setScanState("captured");
    
    // Brief display of captured images
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Process OCR
    processOCR(mockFrontImage, mockBackImage);
  };

  const generateMockBadgeImage = (side) => {
    // Generate a simple mock base64 image placeholder
    // In production, this is replaced by actual USB camera capture
    return `mock_${side}_${Date.now()}`;
  };

  const processOCR = async (frontImage, backImage) => {
    setScanState("processing");
    setProgress(0);
    setProcessingMessage("Checking badge information...");
    
    // Smooth progress simulation with changing messages
    const progressSteps = [
      { progress: 20, message: "Analysing Blue Badge details..." },
      { progress: 40, message: "Reading badge holder name..." },
      { progress: 60, message: "Extracting expiry date..." },
      { progress: 80, message: "Verifying badge number..." },
      { progress: 95, message: "Finalising details..." }
    ];
    
    let currentStep = 0;
    const progressInterval = setInterval(() => {
      if (currentStep < progressSteps.length) {
        setProgress(progressSteps[currentStep].progress);
        setProcessingMessage(progressSteps[currentStep].message);
        currentStep++;
      }
    }, 600);
    
    try {
      const response = await axios.post(`${API}/api/ocr/process`, {
        registration_id: registrationData.registration_id || `temp_${Date.now()}`,
        front_image_base64: frontImage,
        back_image_base64: backImage,
        vehicle_registration: registrationData.vehicle_registration
      });
      
      clearInterval(progressInterval);
      setProgress(100);
      setProcessingMessage("Complete!");
      
      const result = response.data;
      setOcrResult(result);
      
      // Navigate to review page with OCR data
      setTimeout(() => {
        navigate("/kiosk/badge-review", {
          state: {
            registrationData: {
              ...registrationData,
              full_name: result.badge_data?.holder_name || "",
            },
            ocrResult: result,
            badgeImages: {
              front: frontImage,
              back: backImage,
              frontPreview: null,
              backPreview: null
            }
          }
        });
      }, 500);
      
    } catch (error) {
      clearInterval(progressInterval);
      console.error("OCR processing error:", error);
      
      // Still complete the progress smoothly
      setProgress(100);
      setProcessingMessage("Processing complete");
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setScanState("error");
      setErrorMessage(error.response?.data?.detail || "Badge scan could not be completed automatically");
      
      // Still navigate to review but flag for manual review
      setTimeout(() => {
        navigate("/kiosk/badge-review", {
          state: {
            registrationData,
            ocrResult: {
              success: false,
              requires_manual_review: true,
              badge_data: null,
              confidence_score: 0,
              message: "OCR scan failed - manual review required"
            },
            badgeImages: {
              front: frontImage,
              back: backImage
            }
          }
        });
      }, 3000);
    }
  };

  const getAvatarMessage = () => {
    switch (scanState) {
      case "waiting":
        return "Please hold your Blue Badge still while we capture the images. Tap 'Start Scan' when your badge is inserted in the reader.";
      case "countdown":
        return `Capturing your Blue Badge in ${countdown}...`;
      case "capturing":
        return "The cameras are now taking pictures of your badge. Please hold still.";
      case "captured":
        return "Your Blue Badge has now been scanned. Please remove your Blue Badge from the reader.";
      case "processing":
        return "Your Blue Badge details are now being checked. Please wait a moment.";
      case "error":
        return "We had difficulty reading your badge. Don't worry - you can still continue and we'll review it manually.";
      default:
        return "Please insert your Blue Badge into the reader.";
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F0FDF4 100%)' }}
      data-testid="kiosk-badge-scan"
    >
      {/* Header */}
      <header className="flex items-center justify-between p-6 md:p-8">
        <ParkWiseLogo size="lg" />
        
        {/* Progress */}
        <div className="flex items-center gap-2">
          <div className="progress-step completed"><CheckCircle className="w-5 h-5" /></div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step completed"><CheckCircle className="w-5 h-5" /></div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step active">3</div>
          <div className="progress-line w-8"></div>
          <div className="progress-step pending">4</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 pb-8">
        {/* AI Avatar - Only show and speak during certain states */}
        {(scanState === "waiting" || scanState === "captured" || scanState === "processing" || scanState === "error") && (
          <div className="mb-6">
            <AiAvatar 
              message={getAvatarMessage()} 
              size="medium"
              autoSpeak={scanState !== "processing"} // Don't auto-speak during processing
            />
          </div>
        )}

        {/* WAITING STATE - Insert badge prompt */}
        {scanState === "waiting" && (
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Insert Badge & Hold Still
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Place your badge flat in the letterbox slot, then tap Start Scan
            </p>
            
            <div className="w-40 h-40 mx-auto bg-blue-100 rounded-3xl flex items-center justify-center mb-8 border-4 border-blue-300">
              <CreditCard className="w-20 h-20 text-blue-500" />
            </div>
            
            <button
              onClick={startCountdown}
              className="kiosk-btn-primary text-xl px-12 py-5"
              data-testid="start-scan-button"
            >
              Start Scan
            </button>
          </div>
        )}

        {/* COUNTDOWN STATE */}
        {scanState === "countdown" && (
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hold Badge Still
            </h1>
            
            {/* Large countdown display */}
            <div className="relative w-48 h-48 mx-auto mb-8">
              {/* Circular progress */}
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  fill="none"
                  stroke="#E2E8F0"
                  strokeWidth="8"
                />
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 88}
                  strokeDashoffset={2 * Math.PI * 88 * (1 - (COUNTDOWN_START - countdown) / COUNTDOWN_START)}
                  className="transition-all duration-1000"
                />
              </svg>
              {/* Countdown number */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-7xl font-bold text-blue-600">{countdown}</span>
              </div>
            </div>
            
            <p className="text-xl text-slate-700 font-medium">
              Capturing image in <span className="text-blue-600 font-bold">{countdown}</span>...
            </p>
            
            {/* Camera indicators */}
            <div className="flex justify-center gap-8 mt-6">
              <div className="flex items-center gap-2 text-slate-500">
                <Camera className="w-5 h-5 animate-pulse text-blue-500" />
                <span>Top Camera Ready</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <Camera className="w-5 h-5 animate-pulse text-blue-500" />
                <span>Bottom Camera Ready</span>
              </div>
            </div>
          </div>
        )}

        {/* CAPTURING STATE */}
        {scanState === "capturing" && (
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Capturing Images...
            </h1>
            
            <div className="flex justify-center gap-6 mb-8">
              {/* Camera flash animation */}
              <div className="w-32 h-24 bg-white rounded-xl border-4 border-blue-400 flex items-center justify-center animate-pulse">
                <Camera className="w-10 h-10 text-blue-500" />
              </div>
              <div className="w-32 h-24 bg-white rounded-xl border-4 border-blue-400 flex items-center justify-center animate-pulse" style={{ animationDelay: '0.3s' }}>
                <Camera className="w-10 h-10 text-blue-500" />
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-3 text-blue-600">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-lg font-medium">Taking pictures...</span>
            </div>
          </div>
        )}

        {/* CAPTURED STATE - Show images briefly */}
        {scanState === "captured" && (
          <div className="text-center">
            <h1 className="text-3xl font-bold text-green-700 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CheckCircle className="w-10 h-10 inline-block mr-3" />
              Badge Scanned Successfully
            </h1>
            
            <p className="text-xl text-slate-700 mb-6">
              Please remove your Blue Badge from the reader
            </p>
            
            {/* Show captured images */}
            <div className="flex justify-center gap-6 mb-8">
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">Front (Top Camera)</p>
                <div className="w-40 h-28 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl border-2 border-green-400 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">Back (Bottom Camera)</p>
                <div className="w-40 h-28 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl border-2 border-green-400 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Remove badge now</span>
            </div>
          </div>
        )}

        {/* PROCESSING STATE */}
        {scanState === "processing" && (
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Checking Badge Details...
            </h1>
            
            <div className="w-32 h-32 mx-auto mb-6 relative">
              <Loader2 className="w-full h-full text-blue-500 animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <CreditCard className="w-12 h-12 text-blue-600" />
              </div>
            </div>
            
            {/* Dynamic processing message */}
            <p className="text-lg font-medium text-slate-700 mb-4">{processingMessage}</p>
            
            <div className="w-80 mx-auto bg-slate-200 rounded-full h-4 overflow-hidden mb-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-slate-500">{progress}% complete</p>
            
            <p className="text-sm text-slate-400 mt-4">Please wait...</p>
          </div>
        )}

        {/* ERROR STATE */}
        {scanState === "error" && (
          <div className="text-center max-w-md">
            <div className="w-24 h-24 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-12 h-12 text-amber-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Scan Incomplete
            </h1>
            
            <p className="text-slate-600 mb-6">
              {errorMessage || "We couldn't read all the details from your badge. You can still continue - we'll verify manually."}
            </p>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-amber-800 text-sm">
                Your registration may require manual review before approval.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Footer Navigation */}
      <footer className="px-6 py-6 border-t-2 border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/kiosk/badge-intro", { state: { registrationData } })}
            disabled={scanState === "countdown" || scanState === "capturing" || scanState === "processing"}
            className="kiosk-btn-secondary flex items-center gap-3"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>Back</span>
          </button>

          {scanState === "error" && (
            <button
              onClick={() => navigate("/kiosk/badge-review", {
                state: {
                  registrationData,
                  ocrResult: {
                    success: false,
                    requires_manual_review: true,
                    badge_data: null,
                    confidence_score: 0
                  },
                  badgeImages: capturedImages
                }
              })}
              className="kiosk-btn-primary flex items-center gap-3"
              data-testid="continue-anyway-button"
            >
              <span>Continue Anyway</span>
              <ArrowRight className="w-6 h-6" />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
